/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { traceEvent } from '@wk/wk-gatherer';
import cls from 'classnames';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import LeadButton from '@/components/LeadButton';
import Title from '@/components/Title';
import { MyContext } from '@/config/context-manager';
import useGTM from '@/hooks/useGtag';
import { getGa4PageName } from '@/utils/utils';

import footerPaths, { FooterLang, PathsItemProps } from './generatePaths';

const Form = dynamic(() => import('./FooterForm'), {
  ssr: false,
});

const socialMediaList = [
  {
    title: 'facebook',
    ga: 'facebook',
    src: '/images/footer/media-1.png',
    link: 'https://www.facebook.com/wukongedu/',
  },
  {
    title: 'Youtube',
    ga: 'youtube',
    src: '/images/footer/media-2.png',
    link: 'https://www.youtube.com/channel/UC_Ey8x88zFKeJ8pesQ6y_Aw',
  },
  {
    title: 'instagram',
    ga: 'Instagram',
    src: '/images/footer/media-3.png',
    link: 'https://www.instagram.com/wukongchinese/ ',
  },
  {
    title: 'linkedin',
    ga: 'linkedin',
    src: '/images/footer/media-4.png',
    link: 'https://cn.linkedin.com/company/wukong-education',
  },
  {
    title: 'redbook',
    ga: 'redbook',
    src: '/images/footer/media-5.png',
    link: 'https://www.xiaohongshu.com/user/profile/5c23fc910000000005015d94?xhsshare=CopyLink&appuid=5c23fc910000000005015d94&apptime=1607474453',
  },
  {
    title: 'weibo',
    ga: 'weibo',
    src: '/images/footer/media-6.png',
    link: 'https://weibo.com/kiwipedia123?refer_flag=1005055013_',
  },
  {
    title: 'wechat',
    ga: 'wechat',
    src: '/images/footer/media-7.png',
    link: 'https://mp.weixin.qq.com/s/JIPsZoM8z8N8wGESy1gwyg',
  },
];

const honourList = [
  {
    text1: '导航-荣誉-1-1',
    text2: '导航-荣誉-1-2',
  },
  {
    text1: '导航-荣誉-2-1',
    text2: '导航-荣誉-2-2',
  },
  {
    text1: '导航-荣誉-3-1',
    text2: '导航-荣誉-3-2',
  },
];

const paymentMethods = [
  {
    src: '/images/footer/pay-9.png',
    alt: 'WuKong Education Payment Method-Bank Transfer',
  },
  {
    src: '/images/footer/pay-1.png',
    alt: 'WuKong Education Payment Method-Visa',
  },
  {
    src: '/images/footer/pay-2.png',
    alt: 'WuKong Education Payment Method-Mastercard',
  },
  {
    src: '/images/footer/pay-10.png',
    alt: 'WuKong Education Payment Method-American Express',
  },
  {
    src: '/images/footer/pay-discover.png',
    alt: 'WuKong Education Payment Method-Discover',
  },
  {
    src: '/images/footer/pay-3.png',
    alt: 'WuKong Education Payment Method-PayPal',
  },
  // { src: '/images/footer/pay-4.png' },
  {
    src: '/images/footer/pay-5.png',
    alt: 'WuKong Education Payment Method-Alipay',
  },
  {
    src: '/images/footer/pay-6.png',
    alt: 'WuKong Education Payment Method-Wechat Pay',
  },
  {
    src: '/images/footer/pay-7.png',
    alt: 'WuKong Education Payment Method-Klarna',
  },
  // { src: '/images/footer/pay-8.png' },
];

export interface FooterProps {
  tooterTheme?: 'home' | 'base';
}

// 删除 迁移到首页
const LandingHome = () => {
  const { t } = useTranslation(['common', 'path']);
  const router = useRouter();
  // 获取当前路径
  const currentPath = router.pathname;

  return (
    <section className="relative h-[253px] overflow-hidden bg-gradient-to-b from-[#1B1F4D] to-[#561499] lg:-mb-56 lg:h-[583px] xl:-mb-72 xl:h-[800px]">
      <div className="absolute  bottom-0 left-1/2 right-0 top-0 z-10  w-[200%] translate-x-[-50%] lg:left-0 lg:w-full lg:translate-x-0">
        <Image
          src="/images/footer/footer-landing-full-bg.png"
          alt="WuKong"
          sizes="100vw"
          width={1686}
          height={1686}
          className="mx-auto mt-15 lg:mt-20 xl:mt-[100px] "
        />
      </div>
      <div className=" bg-[linear-gradient(180deg,_#1d1f50_13%,rgba(86,_20,_152,_0.5)_100%)] " />
      <div className="from-12.51%  absolute bottom-0 left-0 right-0   top-0 z-20 w-full  bg-gradient-to-b from-[#1D1F50_12.51%] to-[rgba(86,_20,_152,_0.5)_100%] pt-12 lg:to-transparent lg:pt-[120px] xl:pt-40 ">
        <div className="container text-center">
          <Title className="[&]:text-white">{t('尾部留资-标题')}</Title>
          <LeadButton
            traceName="O_CTAModule_CTAClick"
            traceGa4Name={`new_entry_${getGa4PageName(currentPath)}_CTAModuleCTA`}
            target="_blank"
            theme="colorfulBorder"
            size="xl"
            className="mt-6 font-semibold lg:mt-12 xl:mt-15 "
            traceData={{
              page_name: currentPath,
            }}
            htmlLabel="strong">
            {t('尾部留资-按钮')}
          </LeadButton>
        </div>
      </div>
    </section>
  );
};

// 删除 迁移到首页
const LandingBase = () => {
  return <></>;
};

const mail = 'contact@wukongsch.com';
const gaFix = 'O_Footer_';

/**
 * 「袋鼠数学 (Math Kangaroo)」这类「名称 (English)」文案：括号里的英文整体换到下一行，且不再折行。
 * 否则折行位置取决于列宽，可能在英文单词中间断开（如 `(Math` / `Kangaroo)`）。
 * lg 档（1024~1440）这一栏只有 ~90px，英文会略超出列宽，但列间距有 24px，不会和相邻列重叠。
 * 只对设置了 breakBeforeParen 且文案符合该形态的条目生效，其余文案原样输出。
 * 文字内容仍是完整的「名称 (English)」，不影响锚文本。
 */
const renderLabel = ({ label, breakBeforeParen }: PathsItemProps) => {
  const matched = breakBeforeParen ? label.match(/^(.+?)\s+(\(.+\))$/) : null;
  if (!matched) {
    return label;
  }
  return (
    <>
      {`${matched[1]} `}
      <br />
      <span className="whitespace-nowrap">{matched[2]}</span>
    </>
  );
};

const Footer = ({ tooterTheme = 'base' }: FooterProps) => {
  const { framework, locale } = useContext(MyContext);
  const { t, i18n } = useTranslation(['common']);
  const [showICP, setShowICP] = useState(false);
  const sendDataToGTM = useGTM();
  const router = useRouter();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setShowICP(window?.location?.origin?.includes('wukongedu.net'));
    }
  }, [router]);

  // 获取当前路径
  const currentPath = router.pathname;

  const landingTheme = {
    home: LandingHome,
    base: LandingBase,
  };

  // 删除 迁移到首页
  const LandingHtml = landingTheme[tooterTheme];

  const openKaamel = () => {
    if (window?.kaamelObj) {
      window?.kaamelObj?.openPanel();
    }
  };

  // Math 专题列的中英文锚文本/顺序不同，需要按当前语言取配置
  const footerLang: FooterLang = i18n?.language === 'zh' ? 'zh' : 'en';

  const navConfigs = useMemo(() => {
    const oldArr = footerPaths(t, footerLang);
    // 展示cookie设置
    // if (framework && framework.framework && framework.finished) {
    //   oldArr[2]?.children?.push({ label: t('隐私设置'), ga: 'privacySettings', fn: openKaamel });
    // }
    return oldArr;
  }, [t, framework, footerLang]);

  const showFramework = useMemo(() => {
    // 展示cookie设置
    if (framework && framework.framework && framework.finished) {
      return true;
    }
    return false;
  }, [framework]);

  const handleGA = (gaName?: string, extra: { [key: string]: any } = {}) => {
    if (gaName) {
      const gaNameFix = `${gaFix}${gaName}`;
      sendDataToGTM(gaName);
      traceEvent({
        data: {
          _event: gaNameFix,
          // 点击的所有页面名称
          page_name: currentPath,
          ...extra,
        },
      });
    }
  };

  const navNode = (child: PathsItemProps[]) => {
    return (
      <>
        {child?.map((item, i) => {
          return (
            <p
              className="mt-3 cursor-pointer text-sm  opacity-85 transition-opacity hover:opacity-100 lg:text-xs xl:mt-6 xl:text-sm"
              key={i}
              onClick={() => {
                handleGA(item.ga);
                if (item?.fn) {
                  item?.fn();
                }
              }}>
              {!item.href ? (
                <span>{renderLabel(item)}</span>
              ) : (
                <Link
                  href={
                    (item?.hrefZh && i18n?.language === 'zh'
                      ? item?.hrefZh
                      : item.href) || ''
                  }
                  locale={item?.locale}
                  target={item?.target}>
                  {renderLabel(item)}
                </Link>
              )}
            </p>
          );
        })}
      </>
    );
  };

  const title = 'xl:text-base lg:text-sm text-base font-semibold';

  const honourHtml = () => {
    return (
      <div className="flex flex-col items-center justify-center gap-10 md:flex-row md:gap-15 xl:justify-between xl:gap-0">
        {honourList.map((item, index) => (
          <div
            className="flex w-44 justify-between text-center text-xs"
            key={index}>
            <Image
              src="/images/footer/olive.svg"
              width={26}
              height={56}
              alt=""
              className=""
            />
            <div>
              <p
                className="font-semibold"
                dangerouslySetInnerHTML={{ __html: t(item.text1) }}
              />
              <p
                className=" mt-1 opacity-45"
                dangerouslySetInnerHTML={{ __html: t(item.text2) }}
              />
            </div>
            <Image
              src="/images/footer/olive.svg"
              width={26}
              height={56}
              alt=""
              className="rotate-y-180"
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div data-expose-event="O_FooterExpose">
      {/* 删除 迁移到首页 */}
      <LandingHtml />
      <footer className="relative mx-auto text-white lg:max-w-screen-lg lg:px-5 xl:max-w-screen-xl ">
        <div className=" absolute  z-30 hidden lg:-top-36 lg:right-8 lg:block xl:-top-[200px] xl:right-[110px]">
          <Image
            src="/images/footer/footer-landing-ip.png"
            className=" lg:h-[172px] lg:w-[260px]  xl:h-[236px] xl:w-[357px] "
            alt="WuKong"
            width={357}
            height={236}
          />
        </div>
        {/* 移动端：左右/上 20，底部 40（Figma 375）；lg 40；xl 60 */}
        <div className="relative z-20 bg-[#31355F] p-5 pb-10 lg:rounded-5xl lg:p-10 xl:p-15">
          {/* Figma: 1920 卡片内 gap 40、左栏 nav↔付款 60；1080 均为 40 */}
          <div className="flex flex-col gap-10 lg:flex-row xl:gap-10">
            <div className="flex w-full flex-col gap-10 lg:w-[52.3%] xl:w-[53.5%] xl:gap-15">
              <nav className="grid grid-cols-2 gap-x-10 gap-y-10 lg:grid-cols-4 lg:gap-x-6 lg:gap-y-0 xl:gap-x-10 ">
                {navConfigs.map((item, i) => {
                  const childNode = navNode(item.children);
                  return (
                    <div
                      key={i}
                      className="odd:border-r odd:border-white/8 lg:border-none">
                      <p className={cls('mb-6 lg:text-sm xl:mb-10 ', title)}>
                        {item.label}
                      </p>
                      <div>{childNode}</div>
                    </div>
                  );
                })}
              </nav>
              <div className="hidden lg:block">
                <p className={cls(title)}>{t('导航-付款方式')}</p>
                <ul className="mt-4 flex flex-wrap gap-4">
                  {paymentMethods.map((item, i) => (
                    <li key={i} className="shrink-0">
                      <Image
                        src={item.src}
                        alt={item.alt}
                        className="h-12 w-[106px] lg:h-10 lg:w-[88px] xl:h-12 xl:w-[106px]"
                        width={106}
                        height={48}
                        quality={90}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="flex w-full flex-col gap-10 lg:w-[47.7%] lg:border-l lg:border-white/8 lg:pl-10 xl:w-[46.5%]">
              <div>
                <p className={cls(title)}>{t('导航-订阅-标题')}</p>
                <Form />
              </div>
              <div>
                <p className={cls(title)}>{t('导航-下载我们的应用')}</p>
                <div className="mt-4 flex justify-between">
                  <div className="w-[42.85%]">
                    <Link
                      href="/download/"
                      onClick={() => {
                        handleGA('IOSDownloadClick');
                      }}>
                      <Image
                        src={
                          locale === 'en'
                            ? '/images/footer/footer-apple.png'
                            : '/images/footer/footer-apple-zh.png'
                        }
                        width={240}
                        height={80}
                        alt="WuKong class download on the app store"
                      />
                    </Link>
                  </div>
                  <div className="w-[47.5%]">
                    <Link
                      href="/download/"
                      onClick={() => {
                        handleGA('AndroidDownloadClick');
                      }}>
                      <Image
                        width={266}
                        height={80}
                        src={
                          locale === 'en'
                            ? '/images/footer/footer-google.png'
                            : '/images/footer/footer-google-zh.png'
                        }
                        alt="WuKong class download on the google paly"
                      />
                    </Link>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <div id="ksp-seals" />
                {/* <a
                  href="https://www.kidsafeseal.com/certifiedproducts/wukong.html"
                  target="_blank"
                  rel="noreferrer">
                  <span className="visuallyHidden">kidsafe</span>
                  <Image
                    className="m-auto h-[50px] w-36 xl:h-[60px] xl:w-[173px]"
                    src="https://www.kidsafeseal.com/sealimage/886916793933804774/wukong_extralarge_darktm.png"
                    alt="Wukong EDU (web and mobile) is a member of the kidSAFE Seal Program."
                    width={173}
                    height={60}
                  />
                </a> */}
                {/* <p className={cls('mt-4 text-xs lg:text-sm xl:text-base')}>{t('导航-kidsafe')}</p> */}
              </div>
              <ul className="flex flex-wrap justify-center gap-4 lg:flex-nowrap lg:gap-6">
                {socialMediaList.map(item => (
                  <li
                    key={item.title}
                    className="h-11 w-11 opacity-50 transition-opacity hover:opacity-100 lg:h-9 lg:w-9 xl:h-11 xl:w-11"
                    onClick={() => {
                      handleGA('SocialMediaClick', {
                        SocialMedia_name: item.ga,
                      });
                    }}>
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noreferrer nofollow">
                      <Image
                        src={item.src}
                        className="h-11 w-11 rounded-md bg-white lg:h-9 lg:w-9 xl:h-11 xl:w-11"
                        width={44}
                        height={44}
                        alt={`WuKong ${item.title}`}
                      />
                    </a>
                  </li>
                ))}
              </ul>
              <div className="lg:hidden xl:block">{honourHtml()}</div>
            </div>
          </div>
          <div className="mt-10 hidden lg:block xl:hidden">{honourHtml()}</div>
          {/* 联系区：与上方主内容间距 40；移动端行间距 20；地址在上、icon 顶对齐
              PC1920：邮箱文案左缘对齐 EdTech 左侧麦穗（Figma：左列≈付款宽，右列 flex-1 居中） */}
          <ul className="mt-10 flex flex-col gap-5 text-sm font-medium tracking-wide text-white lg:flex-row lg:items-center lg:gap-0 lg:tracking-normal">
            <li className="flex items-start gap-4 lg:w-[52.3%] lg:shrink-0 lg:items-center lg:justify-center xl:w-[53.5%]">
              <Image
                src="/images/footer/icon-address.svg"
                width={16}
                height={16}
                alt="address"
                className="mt-0.5 h-4 w-4 shrink-0 lg:mt-0"
              />
              <p className="flex-1 lg:flex-none">{t('地址-美国-地址')}</p>
            </li>
            <li className="flex items-center gap-4 lg:min-w-0 lg:flex-1 lg:justify-center xl:pl-4">
              <Image
                src="/images/footer/icon-mail.svg"
                width={16}
                height={16}
                alt="mail"
                className="h-4 w-4 shrink-0"
              />
              <a href={`mailto:${mail}`}>{mail}</a>
            </li>
          </ul>
        </div>

        {/* <div className="sticky  bottom-0 left-0 right-0 z-10"> */}
        <div className="">
          <div className="mt-10 flex flex-col items-center px-5 text-sm text-wk-words-1 opacity-85 lg:mt-10 lg:text-xs xl:text-sm">
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:flex-wrap sm:justify-center sm:gap-6">
              <p className="text-wk-words-3">{t('导航-悟空教育年份')}</p>
              <span
                aria-hidden
                className="hidden h-3 w-px bg-wk-words-4/50 sm:inline-block"
              />
              <p className="text-center text-wk-words-3">
                {t('导航-注册商标')}
              </p>
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-4 lg:gap-x-8 xl:mt-6 xl:gap-x-12">
              <Link href="/terms/?layout=no">{t('用户协议')}</Link>
              <span
                aria-hidden
                className="h-3 w-px bg-wk-words-4/50 lg:hidden"
              />
              <Link href="/policy">{t('导航-服务条款')}</Link>
              <span
                aria-hidden
                className="h-3 w-px bg-wk-words-4/50 lg:hidden"
              />
              <Link href="/cookie-policy/">{t('导航-cookie协议')}</Link>
              {showFramework && (
                <span className="cursor-pointer" onClick={openKaamel}>
                  {t('隐私设置')}
                </span>
              )}
              {showICP && (
                <Link
                  className="w-full text-center lg:w-auto"
                  href="https://beian.miit.gov.cn/#/Integrated/index">
                  蜀ICP备2023029067号-3
                </Link>
              )}
            </div>
          </div>
          <div className="mx-auto mt-6 w-[357px] lg:mt-15 lg:w-[476px] xl:mt-[110px] xl:w-[714px]">
            <Image
              src="/images/footer/footer-bg.png"
              alt="WuKong"
              width={724}
              height={240}
              quality={100}
            />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Footer;
